import { useEffect, useState } from "react";
import { listAgentRegistrations } from "../api/ops.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Agent {
  id: string;
  name: string;
  displayName: string;
  status: string;
  version: string;
  lastHeartbeat: string | null;
  capabilities: string[];
  errorMessage: string | null;
}

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: "text-green-400 bg-green-900/30",
  INACTIVE: "text-gray-400 bg-gray-800",
  ERROR: "text-red-400 bg-red-900/30",
  DEPRECATED: "text-yellow-400 bg-yellow-900/30",
};

export function OpsAgentsPage() {
  const { token } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void listAgentRegistrations(token).then((d: unknown) => {
      setAgents((d as { items: Agent[] }).items ?? []);
      setLoading(false);
    });
  }, []);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <h1 className="mb-6 text-2xl font-bold text-white">Registered Agents</h1>
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-3">
            {agents.length === 0 && <p className="text-gray-400">No agents registered yet.</p>}
            {agents.map((agent) => (
              <div key={agent.id} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <span className="font-medium text-white">{agent.displayName}</span>
                    <span className="ml-2 text-xs text-gray-500">{agent.name}</span>
                    <span className="ml-2 text-xs text-gray-600">v{agent.version}</span>
                  </div>
                  <span
                    className={`rounded px-2 py-0.5 text-xs ${STATUS_COLOR[agent.status] ?? "bg-gray-800 text-gray-400"}`}
                  >
                    {agent.status}
                  </span>
                </div>
                {agent.errorMessage && (
                  <p className="mb-2 text-xs text-red-400">{agent.errorMessage}</p>
                )}
                <div className="flex flex-wrap gap-1">
                  {agent.capabilities.map((cap) => (
                    <span
                      key={cap}
                      className="rounded bg-gray-800 px-2 py-0.5 text-xs text-gray-300"
                    >
                      {cap}
                    </span>
                  ))}
                </div>
                {agent.lastHeartbeat && (
                  <p className="mt-2 text-xs text-gray-600">
                    Last heartbeat: {new Date(agent.lastHeartbeat).toLocaleString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
